import { type ComponentType, type DashboardElement } from '../types/dashboard'

import { addElement, removeElement } from '../utils/elementTree'

export const DEFAULT_ELEMENTS: DashboardElement[] = [
  {
    id: '0',
    type: 'container',
    direction: 'vertical',
    children: [
      {
        id: '0-0',
        type: 'container',
        direction: 'vertical',
        children: [
          {
            id: '0-0-0',
            type: 'container',
            direction: 'horizontal',
            children: [{ id: '0-0-0-0', type: 'slot', size: 100, componentType: 'majorIncidents' }],
            size: 100
          },
          {
            id: '0-0-1',
            type: 'container',
            direction: 'horizontal',
            children: [
              { id: '0-0-1-0', type: 'slot', size: 20, componentType: 'openTickets' },
              { id: '0-0-1-1', type: 'slot', size: 20, componentType: 'awaitingResponseTickets' },
              { id: '0-0-1-2', type: 'slot', size: 20, componentType: 'awaitingActionTickets' },
              { id: '0-0-1-3', type: 'slot', size: 20, componentType: 'closedTickets' }
            ],
            size: 100
          }
        ],
        size: 20
      },
      {
        id: '0-1',
        type: 'container',
        direction: 'horizontal',
        children: [
          {
            id: '0-1-0',
            type: 'container',
            direction: 'vertical',
            children: [
              {
                id: '0-1-0-0',
                type: 'container',
                direction: 'horizontal',
                children: [
                  { id: '0-1-0-0-0', type: 'slot', size: 50, componentType: 'azureYearToDate' },
                  { id: '0-1-0-0-1', type: 'slot', size: 50, componentType: 'azureMonthToDate' }
                ],
                size: 0
              },
              {
                id: '0-1-0-1',
                type: 'container',
                direction: 'horizontal',
                children: [
                  { id: '0-1-0-1-0', type: 'slot', size: 50, componentType: 'azureTotalEmissions' }
                  /*{ id: '0-1-0-1-1', type: 'slot', size: 50, componentType: 'majorIncidents' }*/
                ],
                size: 0
              },
              {
                id: '0-1-0-2',
                type: 'slot',
                size: 0,
                componentType: 'keyContacts'
              },
              { id: '0-1-0-3', type: 'slot', size: 0, componentType: 'secureScores' }
              //  { id: '0-1-0-4', type: 'slot', size: 100, componentType: '0-1-0-4-ghost' }
            ],
            size: 50
          },

          {
            id: '0-1-1',
            type: 'container',
            direction: 'vertical',
            children: [
              { id: '0-1-1-0', type: 'slot', size: 0, componentType: 'azure' },
              {
                id: '0-1-1-1',
                type: 'container',
                direction: 'horizontal',
                children: [
                  { id: '0-1-1-1-0', type: 'slot', size: 30, componentType: 'ticketsByPriority' },
                  { id: '0-1-1-1-1', type: 'slot', size: 30, componentType: 'ticketsByStatus' }
                ],
                size: 0
              }
              // { id: '0-1-1-2', type: 'slot', size: 100, componentType: '0-1-1-2-ghost' }
            ],
            size: 50
          }
        ],
        size: 100
      },
      {
        id: '0-2',
        type: 'slot',
        size: 100,
        componentType: 'recentAzureDeployments'
      },
      {
        id: '0-3',
        type: 'container',
        direction: 'horizontal',
        children: [
          { id: '0-3-0', type: 'slot', size: 50, componentType: 'expiringLicenses' },
          { id: '0-3-1', type: 'slot', size: 50, componentType: 'autoRenewingLicenses' }
        ],
        size: 100
      },
      {
        id: '0-4',
        type: 'container',
        direction: 'horizontal',
        children: [
          { id: '0-4-0', type: 'slot', size: 50, componentType: 'completedChanges' },
          { id: '0-4-1', type: 'slot', size: 50, componentType: 'upcomingChanges' }
        ],
        size: 100
      },
      {
        id: '0-5',
        type: 'container',
        direction: 'horizontal',
        children: [
          { id: '0-5-0', type: 'slot', size: 24.75, componentType: 'quickLinks' },
          { id: '0-5-1', type: 'slot', size: 24.75, componentType: 'quickActions' },
          { id: '0-5-2', type: 'slot', size: 50.5, componentType: 'recentFiles' }
        ],
        size: 100
      }
    ],
    size: 100
  }
]

export const mapElementToSlotItemMap = (elements: DashboardElement[]) => {
  const slotItemMap: Array<{ slot: string; item: string }> = []

  const traverse = (el: DashboardElement) => {
    if (el.type === 'slot') {
      slotItemMap.push({ slot: el.id, item: el.componentType })
    } else {
      el.children.forEach(traverse)
    }
  }

  elements.forEach(traverse)
  return slotItemMap
}

const isGhostSlot = (slot: string) => slot.includes('ghost')
const isGhostItem = (item: string) => item.includes('ghost')

export const mapSlotItemMapToElements = (elements: DashboardElement[], slotItemMap: Array<{ slot: string; item: string }>) => {
  const normalSwap = slotItemMap.every(({ slot, item }) => isGhostSlot(slot) === isGhostItem(item))

  if (normalSwap) {
    const slotMap = new Map<string, ComponentType>()
    slotItemMap.forEach(({ slot, item }) => {
      slotMap.set(slot, item)
    })

    const traverseNormally = (el: DashboardElement) => {
      if (el.type === 'slot') {
        el.componentType = slotMap.get(el.id)!
      } else {
        el.children.forEach(traverseNormally)
      }
    }

    elements.forEach(traverseNormally)
    return elements
  }

  // Ghost swap
  const toElement = slotItemMap.find(({ slot, item }) => isGhostSlot(slot) && !isGhostItem(item))
  const fromElement = slotItemMap.find(({ slot, item }) => isGhostItem(item) && !isGhostSlot(slot))

  if (!toElement || !fromElement) {
    console.error('No to or from element found', slotItemMap, toElement, fromElement)
    return elements
  }

  elements = addElement(elements, {
    elementId: toElement.slot.split('_')[0],
    newComponentType: toElement.item.split('_')[0],
    direction: 'bottom'
  })

  elements = removeElement(elements, { elementId: fromElement.slot.split('_')[0] })

  return elements
}

export const AVAILABLE_COMPONENTS = [
  { type: 'majorIncidents', title: 'Major Incidents' },
  { type: 'secureScores', title: 'Secure Scores' },
  { type: 'azure', title: 'Azure' },
  { type: 'keyContacts', title: 'Key Contacts' },
  { type: 'expiringLicenses', title: 'Expiring Licenses' },
  { type: 'autoRenewingLicenses', title: 'Auto-Renewing Licenses' },
  { type: 'awaitingActionTickets', title: 'Awaiting Action Tickets' },
  { type: 'awaitingResponseTickets', title: 'Awaiting Response Tickets' },
  { type: 'openTickets', title: 'Open Tickets' },
  { type: 'closedTickets', title: 'Closed Tickets' },
  { type: 'recentFiles', title: 'Recent Files' },
  { type: 'ticketsByPriority', title: 'Tickets by Priority' },
  { type: 'ticketsByStatus', title: 'Tickets by Status' },
  { type: 'azureTotalEmissions', title: 'Total Carbon Emissions' },
  { type: 'recentAzureDeployments', title: 'Recent Azure Deployments' },
  { type: 'quickLinks', title: 'Quick Links' },
  { type: 'quickActions', title: 'Quick Actions' },
  { type: 'completedChanges', title: 'Completed Changes' },
  { type: 'upcomingChanges', title: 'Upcoming Changes' },
  { type: 'azureYearToDate', title: 'Azure Cost YTD' },
  { type: 'azureMonthToDate', title: 'Azure Cost MTD' }
] as Array<{ type: ComponentType; title: string }>
